import axios from "axios";
import { installOffline } from "@/lib/offline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // NOTE: withCredentials is intentionally OFF. Auth is carried in the
  // Authorization: Bearer header (see interceptor below), not in cookies.
  // Turning it on would force the backend to send a specific Access-Control-
  // Allow-Origin header (not the wildcard `*`), which breaks Capacitor's
  // https://localhost origin on Android.
});

const TOKEN_KEY = "linkly_token";
const REMEMBER_KEY = "linkly_remember"; // "0" = opt-OUT of persistence, else remembered

const isNative = () => !!window.Capacitor?.isNativePlatform?.();

// --- storage helpers --------------------------------------------------------
// Default is "stay logged in": we ONLY drop to sessionStorage when the user
// explicitly opts out (unchecks "Stay logged in" on the login form). This
// makes Google OAuth, email/password, and the native Android bridge all keep
// their sessions across app restarts without each caller having to remember
// to call setRemember(true).
//
// On native (Android/iOS) we always ALSO persist via @capacitor/preferences
// so the token survives the WebView being killed.
const readRemember = () => {
  try { return localStorage.getItem(REMEMBER_KEY) !== "0"; } catch { return true; }
};

export const setRemember = (v) => {
  try {
    if (v) localStorage.removeItem(REMEMBER_KEY);
    else localStorage.setItem(REMEMBER_KEY, "0");
  } catch { /* ignore */ }
};

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const setToken = (t) => {
  const remember = readRemember();
  try {
    if (t) {
      if (remember) {
        localStorage.setItem(TOKEN_KEY, t);
        try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      } else {
        sessionStorage.setItem(TOKEN_KEY, t);
        try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      }
    } else {
      try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    }
  } catch { /* ignore */ }

  // Mirror to native Preferences so a killed WebView can rehydrate on next
  // launch. Fire-and-forget for the sync-signature API — most callers don't
  // await, and localStorage is already durable across app restarts as a
  // backup. If a caller cares about durability (e.g. right before a manual
  // window.location redirect), use setTokenAsync below to actually wait.
  if (isNative()) {
    (async () => {
      try {
        const { Preferences } = await import("@capacitor/preferences");
        if (t) await Preferences.set({ key: TOKEN_KEY, value: t });
        else await Preferences.remove({ key: TOKEN_KEY });
      } catch { /* ignore */ }
    })();
  }
};

/** Same as setToken but returns a Promise that resolves after the native
 * Preferences write completes — use this from OAuth callbacks that redirect
 * immediately after (which would otherwise race the async mirror). */
export const setTokenAsync = async (t) => {
  setToken(t);
  if (!isNative()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    if (t) await Preferences.set({ key: TOKEN_KEY, value: t });
    else await Preferences.remove({ key: TOKEN_KEY });
  } catch { /* ignore */ }
};

/**
 * On native cold-start, pull the token out of secure Preferences and seed it
 * into localStorage so the axios interceptor (synchronous) can attach it.
 * Returns the token (or null) once bootstrap completes.
 */
export const bootstrapNativeToken = async () => {
  if (!isNative()) return getToken();
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value) {
      // Native always "remembers" — clear any explicit opt-out and mirror
      // the token into localStorage so the axios interceptor (synchronous)
      // can attach it.
      try { localStorage.removeItem(REMEMBER_KEY); } catch { /* ignore */ }
      try { localStorage.setItem(TOKEN_KEY, value); } catch { /* ignore */ }
      return value;
    }
    // Preferences was empty but localStorage might still hold a token from a
    // previous login where the fire-and-forget mirror lost a race with app
    // close. Mirror it BACK into Preferences so next cold-start finds it.
    const local = (() => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } })();
    if (local) {
      try { await Preferences.set({ key: TOKEN_KEY, value: local }); } catch { /* ignore */ }
      return local;
    }
  } catch { /* ignore */ }
  return null;
};

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

installOffline(api, { base: API, getToken });
